import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Modal, Row, Typography, Button } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import { CheckOutlined } from '@ant-design/icons';

import {
  ListStockItemsQuery,
  StockItem,
  StockItemType,
  StockOrderColumnConfig,
} from '@/core/domain/stock/StockItems/StockItems.types';

import { useStockItems } from '@/core/domain/stock/StockItems/StockItems.hooks';

import { ModalProps } from '@/types/ModalProps';

import FiltersItemsStockModal from './FiltersItemsStockModal';
import { getRowKey } from '@/core/domain/stock/StockItems/StockItems.utils';
import { SorterResult } from 'antd/lib/table/interface';
import { tableSorterOrder } from '@/utils/table';
import { PartItemFieldOption } from '@/core/domain/stock/Configuration/Configuration.types';
import InfiniteTable from '../InfiniteTable';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import { ListInvoiceItemQuery } from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';
import StatusTag from '@/components/_common/StatusTag';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';
import { useDisclosure, useWindowSize } from '@/hooks';
import PartSimpliedRegisterModal, {
  OnCallbackAddPart,
} from '../PartSimpliedRegisterModal';
import ConsumablesSimpliedRegisterModal from '../ConsumablesSimpliedRegisterModal';

const { Title, Text } = Typography;

interface Props extends ModalProps {
  invoiceNoteItems?: ListInvoiceItemQuery[];
  itemType?: StockItemType;
  onSelectItem: (item: StockItem) => void;
  itemsSelected?: number[];
  isPurchase?: boolean;
}

const ItemsStockModal: React.FC<Props> = ({
  visible,
  handleVisible,
  itemType,
  onSelectItem,
  itemsSelected,
  isPurchase,
}: Props) => {
  const intl = useIntl();

  const { isMobile } = useWindowSize();

  const [queryParams, setQueryParams] = useState<ListStockItemsQuery>(
    itemType
      ? {
          itemType,
        }
      : {},
  );

  const [callbackPart, setCallbackPart] = useState<OnCallbackAddPart>();
  const [partCreate, actionPartCreate] = useDisclosure(false, {
    onOpen() {
      setCallbackPart(undefined);
    },
  });
  const [modalConsumableOpen, modalConsumableAction] = useDisclosure(false);

  useEffect(() => {
    if (callbackPart) {
      setQueryParams((prev) => ({
        ...prev,
        search: callbackPart.part.name,
      }));
    }
  }, [callbackPart]);

  useEffect(() => {
    setQueryParams({ itemType });
  }, [itemType]);

  const { data, isFetching, hasNextPage, fetchNextPage } = useStockItems(
    visible,
    { ...queryParams, active: true },
  );

  const handleCancel = () => {
    setQueryParams(
      itemType
        ? {
            itemType,
          }
        : {},
    );
    handleVisible();
  };

  function handleOrder(sorter: SorterResult<StockItem>) {
    if (sorter && sorter?.columnKey) {
      setQueryParams((prev) => {
        if (sorter && sorter?.columnKey) {
          const order = sorter.order
            ? tableSorterOrder[sorter.order]
            : undefined;

          if (prev.orderColumns === StockOrderColumnConfig[sorter.columnKey]) {
            return {
              ...prev,
              order,
            };
          }

          return {
            ...prev,
            order: tableSorterOrder.ascend,
            orderColumns: StockOrderColumnConfig[sorter.columnKey],
          };
        }

        return prev;
      });
    }
  }

  const columns: ColumnsType<StockItem> = [
    itemType === StockItemType.Consumable
      ? useConfigurationColumnsPart({ internalCode: 'sku' })
      : useConfigurationColumnsPart({ internalCode: 'sku' }),
    {
      ellipsis: true,
      dataIndex: 'description',
      key: PartItemFieldOption.Description,
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.itemName',
      }),
      width: 300,
      sorter: true,
    },
    {
      ellipsis: true,
      width: 120,
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.type',
      }),
      render(row: StockItem) {
        let label = '-';

        if (row.partId) {
          label = intl.formatMessage({
            id: 'stockCorrections.domain.items.type.item_type.part',
          });
        } else if (row.consumableId) {
          label = intl.formatMessage({
            id: 'stockCorrections.domain.items.type.item_type.consumable',
          });
        }

        return <StatusTag label={label} color="blue" />;
      },
    },
    {
      dataIndex: 'actions',
      align: 'right',
      render: (_, row) => (
        <Button
          disabled={itemsSelected?.includes(row.partId ?? row.consumableId)}
          type="primary"
          onClick={() => onSelectItem(row)}
          icon={<CheckOutlined />}
        ></Button>
      ),
      width: 90,
    },
  ];

  const stockItems = flatInfiniteResult(data);

  return (
    <Modal
      title={intl.formatMessage({ id: 'stockCorrections.domain.items.add' })}
      open={visible}
      onCancel={handleCancel}
      width={850}
      okButtonProps={{ style: { display: 'none' } }}
      cancelText={intl.formatMessage({ id: 'commons.form.button.close' })}
      destroyOnClose
    >
      <>
        <Row gutter={16}>
          <Col flex="1">
            <div style={{ width: '100%' }}>
              <div style={{ marginBottom: '24px' }}>
                <Title level={4}>
                  <FormattedMessage id="stockCorrections.create.items.search" />
                </Title>
                <Text>
                  {isPurchase ? (
                    'Filtre e selecione os itens a serem adicionados na compra'
                  ) : (
                    <FormattedMessage id="stockCorrections.create.items.searchMessage" />
                  )}
                </Text>
              </div>

              <FiltersItemsStockModal
                setQueryParams={setQueryParams}
                itemType={itemType}
              />

              <Row justify="end" align="middle" gutter={[12, 12]}>
                {itemType === 1 && (
                  <Col span={isMobile ? 24 : undefined}>
                    <Button
                      type="primary"
                      block
                      onClick={actionPartCreate.open}
                    >
                      Cadastrar peça
                    </Button>
                  </Col>
                )}

                {itemType === 2 && (
                  <Col span={isMobile ? 24 : undefined}>
                    <Button
                      type="primary"
                      block
                      onClick={modalConsumableAction.open}
                    >
                      Cadastrar insumo
                    </Button>
                  </Col>
                )}
              </Row>

              <InfiniteTable
                columns={columns}
                onChange={(_p, _f, sorter) => {
                  if (!Array.isArray(sorter)) {
                    handleOrder(sorter);
                  }
                }}
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
                dataSource={stockItems}
                loading={isFetching}
                className="scroll-table"
                rowKey={(row) => getRowKey(row)}
              />
            </div>
          </Col>
        </Row>

        <PartSimpliedRegisterModal
          open={partCreate}
          onCancel={actionPartCreate.close}
          onCallback={setCallbackPart}
        />

        <ConsumablesSimpliedRegisterModal
          open={modalConsumableOpen}
          onCancel={modalConsumableAction.close}
        />
      </>
    </Modal>
  );
};

export default ItemsStockModal;
